import { DEFAULT_PAGE_SIZE } from "@/constants/config";
import {
  BUNDLE_DEFAULTS,
  DEFAULT_PRODUCT_UNIT,
  PRODUCT_KINDS,
  PRODUCT_KIND_LABELS,
  PRODUCT_UNITS,
} from "@/constants/products";
import { STOCK_MOVEMENT_LABELS } from "@/constants/stock";
import { PageHeader } from "@/components/shared/page-header";
import { Pagination } from "@/components/shared/pagination";
import { EmptyState, ErrorState, LoadingState } from "@/components/shared/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Field, Input, Select, Textarea } from "@/components/ui/field";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { formatCurrency, formatDateTime } from "@/lib/format";
import {
  useCreateProduct,
  useCreateVariant,
  useDeleteProduct,
  useProduct,
  useProducts,
  useStockMovements,
  useUpdateProduct,
  type ProductComponentInput,
  type ProductListItem,
} from "@/features/products/api";
import { useWorkspace } from "@/providers/workspace-provider";
import type { ProductKind } from "@bizy/core";
import { History, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface ProductFormState {
  name: string;
  sku: string;
  description: string;
  kind: ProductKind;
  price: string;
  stock: string;
  unit: string;
  is_active: boolean;
}

const EMPTY_FORM: ProductFormState = {
  name: "",
  sku: "",
  description: "",
  kind: PRODUCT_KINDS.sale,
  price: "0",
  stock: "0",
  unit: DEFAULT_PRODUCT_UNIT,
  is_active: true,
};

/** `/w/:slug/products` — catalog CRUD plus per-product variants. */
export const ProductsPage = () => {
  const { workspace, settings } = useWorkspace();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<ProductListItem | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<ProductFormState>(EMPTY_FORM);
  const [components, setComponents] = useState<ProductComponentInput[]>([]);
  const [historyFor, setHistoryFor] = useState<ProductListItem | null>(null);
  const [variantFor, setVariantFor] = useState<ProductListItem | null>(null);
  const [variantName, setVariantName] = useState("");
  const [variantPrice, setVariantPrice] = useState("0");
  const debouncedSearch = useDebouncedValue(search);

  const productsQuery = useProducts(workspace.id, {
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    q: debouncedSearch.length > 0 ? debouncedSearch : undefined,
  });
  const createProduct = useCreateProduct(workspace.id);
  const updateProduct = useUpdateProduct(workspace.id);
  const deleteProduct = useDeleteProduct(workspace.id);
  const createVariant = useCreateVariant(workspace.id);
  const variantsQuery = useProduct(workspace.id, variantFor?.id);
  const movementsQuery = useStockMovements(workspace.id, historyFor?.id);
  const componentOptions = useProducts(workspace.id, { pageSize: 100, activeOnly: true });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setComponents([]);
    setFormOpen(true);
  };

  const openEdit = (product: ProductListItem) => {
    setEditing(product);
    setForm({
      name: product.name,
      sku: product.sku ?? "",
      description: product.description ?? "",
      kind: product.kind,
      price: String(product.price),
      stock: String(product.stock),
      unit: product.unit,
      is_active: product.is_active,
    });
    setComponents(
      product.components.map((component) => ({
        component_product_id: component.product_id,
        component_variant_id: component.variant_id,
        quantity: component.quantity,
      })),
    );
    setFormOpen(true);
  };

  const onSubmit = async () => {
    const isBundle = form.kind === PRODUCT_KINDS.bundle;
    if (isBundle && components.length === 0) {
      toast.error("Add at least one component to the bundle.");
      return;
    }

    const payload = {
      name: form.name.trim(),
      sku: form.sku.trim().length > 0 ? form.sku.trim() : null,
      description: form.description.trim().length > 0 ? form.description.trim() : null,
      kind: form.kind,
      price: Number(form.price) || 0,
      stock: isBundle ? 0 : Number(form.stock) || 0,
      unit: form.unit,
      is_active: form.is_active,
      ...(isBundle ? { components } : {}),
    };

    try {
      if (editing) {
        await updateProduct.mutateAsync({ productId: editing.id, patch: payload });
        toast.success("Product updated.");
      } else {
        await createProduct.mutateAsync(payload);
        toast.success("Product created.");
      }
      setFormOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save the product.");
    }
  };

  const items = productsQuery.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Products"
        description={`Catalog and inventory · ${workspace.plan} plan`}
        actions={
          <Button onClick={openCreate}>
            <Plus className="size-4" aria-hidden />
            New product
          </Button>
        }
      />

      <Input
        placeholder="Search products…"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setPage(1);
        }}
        className="max-w-xs"
      />

      <Card>
        <CardContent className="p-0">
          {productsQuery.isLoading ? <LoadingState /> : null}
          {productsQuery.isError ? (
            <ErrorState
              description={
                productsQuery.error instanceof Error ? productsQuery.error.message : undefined
              }
              onRetry={() => void productsQuery.refetch()}
            />
          ) : null}
          {!productsQuery.isLoading && items.length === 0 ? (
            <EmptyState
              title="No products yet"
              description="Add your first product to start selling."
              action={<Button onClick={openCreate}>Add product</Button>}
            />
          ) : null}
          {items.length > 0 && productsQuery.data ? (
            <div className="p-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">Stock</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>
                        <p className="font-medium">{product.name}</p>
                        {product.sku ? (
                          <p className="text-xs text-muted-foreground">{product.sku}</p>
                        ) : null}
                      </TableCell>
                      <TableCell>{PRODUCT_KIND_LABELS[product.kind]}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(product.price, settings.currency)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {product.kind === PRODUCT_KINDS.bundle
                          ? `~${product.availability ?? 0} ${product.unit} buildable`
                          : `${product.stock} ${product.unit}`}
                      </TableCell>
                      <TableCell>
                        <Badge variant={product.is_active ? "success" : "outline"}>
                          {product.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setVariantFor(product)}>
                            Variants
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Stock history"
                            onClick={() => setHistoryFor(product)}
                          >
                            <History className="size-4" aria-hidden />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openEdit(product)}>
                            <Pencil className="size-4" aria-hidden />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-danger"
                            onClick={async () => {
                              try {
                                await deleteProduct.mutateAsync(product.id);
                                toast.success("Product deleted.");
                              } catch (error) {
                                toast.error(
                                  error instanceof Error ? error.message : "Could not delete.",
                                );
                              }
                            }}
                          >
                            <Trash2 className="size-4" aria-hidden />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                page={productsQuery.data.page}
                pageSize={productsQuery.data.page_size}
                total={productsQuery.data.total}
                onPageChange={setPage}
              />
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? "Edit product" : "New product"}
        footer={
          <>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              Cancel
            </Button>
            <Button loading={createProduct.isPending || updateProduct.isPending} onClick={() => void onSubmit()}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field label="Name" htmlFor="product-name" required>
            <Input
              id="product-name"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="SKU" htmlFor="product-sku">
              <Input
                id="product-sku"
                value={form.sku}
                onChange={(event) => setForm({ ...form, sku: event.target.value })}
              />
            </Field>
            <Field label="Kind" htmlFor="product-kind">
              <Select
                id="product-kind"
                value={form.kind}
                onChange={(event) => setForm({ ...form, kind: event.target.value as ProductKind })}
              >
                {Object.values(PRODUCT_KINDS).map((kind) => (
                  <option key={kind} value={kind}>
                    {PRODUCT_KIND_LABELS[kind]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Price" htmlFor="product-price" required>
              <Input
                id="product-price"
                inputMode="decimal"
                value={form.price}
                onChange={(event) => setForm({ ...form, price: event.target.value })}
              />
            </Field>
            <Field
              label="Stock"
              htmlFor="product-stock"
              hint={form.kind === PRODUCT_KINDS.bundle ? "Derived from components" : undefined}
            >
              <Input
                id="product-stock"
                inputMode="numeric"
                disabled={form.kind === PRODUCT_KINDS.bundle}
                value={form.kind === PRODUCT_KINDS.bundle ? "0" : form.stock}
                onChange={(event) => setForm({ ...form, stock: event.target.value })}
              />
            </Field>
            <Field label="Unit" htmlFor="product-unit">
              <Select
                id="product-unit"
                value={form.unit}
                onChange={(event) => setForm({ ...form, unit: event.target.value })}
              >
                {PRODUCT_UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <Field label="Description" htmlFor="product-description">
            <Textarea
              id="product-description"
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
          </Field>

          {form.kind === PRODUCT_KINDS.bundle ? (
            <div className="space-y-2 rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Components</p>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={components.length >= BUNDLE_DEFAULTS.maxComponents}
                  onClick={() =>
                    setComponents([
                      ...components,
                      {
                        component_product_id: componentOptions.data?.items[0]?.id ?? "",
                        component_variant_id: null,
                        quantity: 1,
                      },
                    ])
                  }
                >
                  <Plus className="size-3" aria-hidden />
                  Add
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Each bundle unit consumes these quantities; availability is the scarcest
                component.
              </p>

              {components.length === 0 ? (
                <p className="text-sm text-muted-foreground">No components yet.</p>
              ) : null}

              {components.map((component, index) => (
                <div key={`${component.component_product_id}-${index}`} className="flex items-end gap-2">
                  <div className="flex-1">
                    <Select
                      aria-label="Component"
                      value={component.component_product_id}
                      onChange={(event) =>
                        setComponents(
                          components.map((entry, position) =>
                            position === index
                              ? {
                                  ...entry,
                                  component_product_id: event.target.value,
                                  component_variant_id: null,
                                }
                              : entry,
                          ),
                        )
                      }
                    >
                      <option value="">Select a product…</option>
                      {(componentOptions.data?.items ?? [])
                        .filter(
                          (option) =>
                            option.kind !== PRODUCT_KINDS.bundle && option.id !== editing?.id,
                        )
                        .map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name} · stock {option.stock}
                          </option>
                        ))}
                    </Select>
                  </div>
                  <Input
                    aria-label="Quantity"
                    inputMode="numeric"
                    className="w-20"
                    value={String(component.quantity)}
                    onChange={(event) =>
                      setComponents(
                        components.map((entry, position) =>
                          position === index
                            ? {
                                ...entry,
                                quantity: Math.min(
                                  BUNDLE_DEFAULTS.maxComponentQuantity,
                                  Math.max(1, Math.trunc(Number(event.target.value) || 1)),
                                ),
                              }
                            : entry,
                        ),
                      )
                    }
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-danger"
                    onClick={() =>
                      setComponents(components.filter((_, position) => position !== index))
                    }
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </Dialog>

      <Dialog
        open={variantFor !== null}
        onClose={() => setVariantFor(null)}
        title={`Variants · ${variantFor?.name ?? ""}`}
        description="Custom variants are a Pro feature; the Free plan allows a single variant."
      >
        <div className="space-y-4">
          <div className="space-y-2">
            {(variantsQuery.data?.variants ?? []).map((variant) => (
              <div key={variant.id} className="flex items-center justify-between rounded-lg border border-border p-2 text-sm">
                <span>{variant.name}</span>
                <span className="tabular-nums">
                  {formatCurrency(variant.price, settings.currency)}
                </span>
              </div>
            ))}
            {variantsQuery.isLoading ? <LoadingState label="Loading variants…" /> : null}
            {!variantsQuery.isLoading && (variantsQuery.data?.variants.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">No variants yet.</p>
            ) : null}
          </div>

          <div className="space-y-3 border-t border-border pt-3">
            <Field label="Variant name" htmlFor="variant-name">
              <Input
                id="variant-name"
                value={variantName}
                onChange={(event) => setVariantName(event.target.value)}
              />
            </Field>
            <Field label="Variant price" htmlFor="variant-price">
              <Input
                id="variant-price"
                inputMode="decimal"
                value={variantPrice}
                onChange={(event) => setVariantPrice(event.target.value)}
              />
            </Field>
            <Button
              className="w-full"
              loading={createVariant.isPending}
              disabled={variantFor === null}
              onClick={async () => {
                if (!variantFor) return;
                try {
                  await createVariant.mutateAsync({
                    productId: variantFor.id,
                    payload: { name: variantName.trim(), price: Number(variantPrice) || 0 },
                  });
                  setVariantName("");
                  setVariantPrice("0");
                  toast.success("Variant added.");
                } catch (error) {
                  toast.error(error instanceof Error ? error.message : "Could not add the variant.");
                }
              }}
            >
              Add variant
            </Button>
          </div>
        </div>
      </Dialog>
      <Dialog
        open={historyFor !== null}
        onClose={() => setHistoryFor(null)}
        title={`Stock history · ${historyFor?.name ?? ""}`}
        description="Every reservation, return and manual correction, newest first."
      >
        <div className="space-y-2">
          {movementsQuery.isLoading ? <LoadingState label="Loading movements…" /> : null}
          {!movementsQuery.isLoading && (movementsQuery.data?.items.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No movements recorded yet.</p>
          ) : null}
          {(movementsQuery.data?.items ?? []).map((movement) => (
            <div
              key={movement.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-border p-2 text-sm"
            >
              <div>
                <p className="font-medium">
                  {STOCK_MOVEMENT_LABELS[movement.reason]} · {movement.quantity}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDateTime(movement.created_at)}
                  {movement.note ? ` · ${movement.note}` : ""}
                </p>
              </div>
              <span
                className={
                  movement.delta > 0
                    ? "tabular-nums text-success"
                    : movement.delta < 0
                      ? "tabular-nums text-danger"
                      : "tabular-nums text-muted-foreground"
                }
              >
                {movement.delta > 0 ? `+${movement.delta}` : movement.delta}
              </span>
            </div>
          ))}
        </div>
      </Dialog>

    </>
  );
};
